import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@retracedgmbh/ui-components-lib';
import { useAuth } from '@state/auth';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOpenFrontendV2 } from '@/hooks/useOpenFrontendV2';
import useTranslator from '@/hooks/useTranslator';
import { hasAnsweredV2Prompt, setV2PromptDismissed } from '@/lib/v2OptInState';

const FRONTEND_V2_URL = import.meta.env.VITE_FRONTEND_V2_URL;
const BANNER_ENABLED =
  import.meta.env.VITE_IS_FRONTEND_V2_BANNER_ENABLED === 'true';
// Same switch frontend-v2 uses for its tours. This dialog is modal, so an E2E
// run that meets it has every click swallowed until something dismisses it —
// and the old-frontend specs log in fresh, so they meet it every time.
const ONBOARDING_ENABLED =
  import.meta.env.VITE_IS_ONBOARDING_ENABLED !== 'false';

/**
 * Announces the new dashboard once, to users who have never opened it.
 *
 * Shown instead of relying on the navbar banner alone, which is easy to look
 * past. Both routes to v2 go through `useOpenFrontendV2`, so this counts towards
 * the same adoption metric.
 *
 * An `AlertDialog`, not a `Dialog`: it asks for a decision, so it should not be
 * dismissable by an outside click or Escape — the two answers are the only ways
 * out, which is also what makes "announced once" honest.
 *
 * Eligibility follows `FrontendV2Banner` — same env flags — with two additions:
 * never for someone who has already opted in, and never twice.
 *
 * One deliberate divergence: the banner now also shows on `/management` once v2
 * has that area switched on, this dialog still does not. It is a blocking
 * `AlertDialog` announcing a launch, and throwing that over an operator
 * mid-task in an admin tool interrupts work the banner can offer without
 * interrupting. Staff reach v2 by the banner there, or by being forwarded on
 * their next login.
 */
export default function V2LaunchDialog() {
  const { getTranslation } = useTranslator({ page: 'navbar' });
  const { pathname } = useLocation();
  const me = useAuth(state => state.me);
  const { open: openV2, opening } = useOpenFrontendV2('launch_dialog');

  // Read once on mount: the value is written by this component or by the act of
  // leaving for v2, so re-reading it mid-session would only ever close the
  // dialog underneath the user.
  const [suppressed, setSuppressed] = useState(hasAnsweredV2Prompt);

  const visible =
    ONBOARDING_ENABLED &&
    BANNER_ENABLED &&
    !!FRONTEND_V2_URL &&
    !!me?.id &&
    !suppressed &&
    !pathname.startsWith('/management');

  function handleDismiss() {
    setV2PromptDismissed();
    setSuppressed(true);
  }

  return (
    <AlertDialog open={visible}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {getTranslation('navbar.v2Launch.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {getTranslation('navbar.v2Launch.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* The library's footer ships `space-x-2` and no top margin, which reads
            as cramped against a three-line description. `space-x-0` first, since
            `space-x` and `gap` would otherwise stack into an 8px + 12px gutter. */}
        <AlertDialogFooter className='tw:mt-3 tw:sm:space-x-0 tw:sm:gap-3'>
          {/* Action and Cancel already carry the button styling, so they are
              used directly rather than wrapping a <Button>. */}
          <AlertDialogCancel onClick={handleDismiss}>
            {getTranslation('navbar.v2Launch.later')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={openV2} disabled={opening}>
            {getTranslation('navbar.v2Launch.cta')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
